import { StoreEvent } from "src/interfaces/event.interface";

const EMPTY = "\u2013";

export interface EventsTableProps {
  events: StoreEvent[];
  onEdit: (event: StoreEvent) => void;
  onDelete: (correlationId: string) => void;
}

function formatSingleDate(isoDate: string): string {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  return `${m}/${d}/${y}`;
}

function formatSingleTime(seconds: number): string {
  if (seconds == null && seconds !== 0) return "";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const ampm = hours >= 12 ? "PM" : "AM";
  const h = hours % 12 || 12;
  const m = String(minutes).padStart(2, "0");
  return `${h}:${m} ${ampm}`;
}

function getRecurrenceLabel(recurrences: { recurrenceRule: string }[]): string {
  if (!recurrences?.length) return "";
  const rule = recurrences[0]?.recurrenceRule ?? "";
  if (rule.includes("FREQ=DAILY")) return "Daily";
  if (rule.includes("FREQ=WEEKLY")) return "Weekly";
  if (rule.includes("FREQ=MONTHLY")) return "Monthly";
  return recurrences.length > 1 ? `${recurrences.length} rules` : "Custom";
}

const TABLE_MIN_WIDTH = 720;
const headerCellClass =
  "py-3 align-middle text-left text-[12px] font-semibold uppercase tracking-wide text-[#374151] border-b border-[#e2e8f0] bg-[#f1f5f9] whitespace-nowrap";

const stickyHeaderClass = "sticky top-0 z-10";

const EventsTable = ({ events, onEdit, onDelete }: EventsTableProps) => {
  return (
    <div className="w-full h-full flex flex-col min-h-0 bg-white rounded-xl border border-[#F1F1F2] shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-hidden">
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="w-full text-left border-collapse table-fixed" style={{ minWidth: TABLE_MIN_WIDTH }}>
          <colgroup>
            <col style={{ width: "48px" }} />
            <col style={{ width: "160px" }} />
            <col style={{ width: "180px" }} />
            <col style={{ width: "140px" }} />
            <col style={{ width: "120px" }} />
            <col style={{ width: "70px" }} />
            <col style={{ width: "90px" }} />
            <col style={{ width: "120px" }} />
          </colgroup>
          <thead>
            <tr>
              <th className={`pl-4 pr-2 ${stickyHeaderClass} ${headerCellClass}`}>#</th>
              <th className={`pl-2 pr-2 ${stickyHeaderClass} ${headerCellClass}`}>Name</th>
              <th className={`pl-2 pr-2 ${stickyHeaderClass} ${headerCellClass}`}>Description</th>
              <th className={`pl-2 pr-2 ${stickyHeaderClass} ${headerCellClass}`}>Date range</th>
              <th className={`pl-2 pr-2 ${stickyHeaderClass} ${headerCellClass}`}>Time</th>
              <th className={`pl-2 pr-2 ${stickyHeaderClass} ${headerCellClass}`}>Items</th>
              <th className={`pl-2 pr-2 ${stickyHeaderClass} ${headerCellClass}`}>Recurrence</th>
              <th className={`pl-2 pr-4 ${stickyHeaderClass} ${headerCellClass}`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event, index) => {
              const itemCount = event.eventItems?.length ?? 0;
              const recurrenceLabel = getRecurrenceLabel(
                event.recurrences ?? []
              );
              const startDate = event.startDate ?? "";
              const endDate = event.endDate ?? "";
              const hasDateRange = startDate && endDate;
              const startTimeSec = Number(event.startTimeInSeconds ?? 0);
              const endTimeSec = Number(event.endTimeInSeconds ?? 0);
              const hasTimeRange =
                event.startTimeInSeconds != null && event.endTimeInSeconds != null;
              const isInactive = event.active === false;
              return (
                <tr
                  key={event.correlationId}
                  className={`border-t border-[#f1f5f9] text-sm text-[#111827] hover:bg-[#f3f4f6] cursor-pointer transition-colors group ${
                    isInactive ? "opacity-[0.92] bg-[#fafafa]" : ""
                  }`}
                  onClick={() => onEdit(event)}
                >
                  <td className="py-3 pl-4 pr-2 align-middle font-medium">
                    {index + 1}
                  </td>
                  <td className="py-3 pl-2 pr-2 align-middle font-medium">
                    <div className="flex flex-col gap-1.5 min-w-0">
                      <span className="truncate">
                        {event.eventName ?? (
                          <span className="text-[#9CA3AF] font-normal">
                            {EMPTY}
                          </span>
                        )}
                      </span>
                      {isInactive ? (
                        <span className="inline-flex items-center w-fit text-[11px] text-[#92400E] font-medium rounded-full border border-[#FCD34D] bg-[#FFFBEB] py-0.5 px-2">
                          Inactive
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td
                    className="py-3 pl-2 pr-2 align-middle text-[#6b7280] align-top"
                    title={event.eventDescription ?? undefined}
                  >
                    {event.eventDescription ? (
                      <span className="line-clamp-3 block break-words">
                        {event.eventDescription}
                      </span>
                    ) : (
                      <span className="text-[#9CA3AF] font-normal">
                        {EMPTY}
                      </span>
                    )}
                  </td>
                  <td className="py-3 pl-2 pr-2 align-middle tabular-nums">
                    {hasDateRange ? (
                      <span className="inline-flex flex-col items-start justify-center gap-0 leading-tight">
                        <span>{formatSingleDate(startDate)}</span>
                        <span className="text-[#6B7280] self-center">–</span>
                        <span>{formatSingleDate(endDate)}</span>
                      </span>
                    ) : (
                      <span className="text-[#9CA3AF] font-normal">
                        {EMPTY}
                      </span>
                    )}
                  </td>
                  <td className="py-3 pl-2 pr-2 align-middle tabular-nums">
                    {hasTimeRange ? (
                      <span className="inline-flex flex-col items-start justify-center gap-0 leading-tight">
                        <span>{formatSingleTime(startTimeSec)}</span>
                        <span className="text-[#6B7280] self-center">–</span>
                        <span>{formatSingleTime(endTimeSec)}</span>
                      </span>
                    ) : (
                      <span className="text-[#9CA3AF] font-normal">
                        {EMPTY}
                      </span>
                    )}
                  </td>
                  <td
                    className="py-3 pl-2 pr-2 align-middle tabular-nums font-medium"
                  >
                    {itemCount > 0 ? (
                      itemCount
                    ) : (
                      <span className="text-[#9CA3AF] font-normal">
                        {EMPTY}
                      </span>
                    )}
                  </td>
                  <td className="py-3 pl-2 pr-2 align-middle whitespace-nowrap">
                    {recurrenceLabel ? (
                      recurrenceLabel
                    ) : (
                      <span className="text-[#9CA3AF] font-normal">
                        {EMPTY}
                      </span>
                    )}
                  </td>
                  <td
                    className="py-3 pl-2 pr-4 align-middle"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-start gap-0.5">
                      <button
                        type="button"
                        className="p-2 rounded-full text-[#6B7280] opacity-80 hover:opacity-100 hover:bg-[#f3f4f6] hover:text-[#111827] transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(event);
                        }}
                        aria-label="Edit event"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className="w-5 h-5"
                        >
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="p-2 rounded-full text-[#6B7280] opacity-80 hover:opacity-100 hover:bg-red-50 hover:text-[#DC2626] transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (event.correlationId)
                            onDelete(event.correlationId);
                        }}
                        aria-label="Delete event"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className="w-5 h-5"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.518.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EventsTable;
